import { useEffect, useState } from "react";
import { api, type Me, type Session } from "../api.js";
import { BackupLink } from "../Frame.js";

/**
 * FR-SESS-4, SESS-05 — creating an ad-hoc session is one screen, with
 * everything pre-filled. Nothing here is required: the button works on its own,
 * and the fields exist for the session that is not like last week's.
 */
export function StartSession({
  me,
  onOpen,
  onSignedOut,
}: {
  me: Me;
  onOpen: (sessionId: string) => void;
  onSignedOut: () => void;
}): React.JSX.Element {
  const [sessions, setSessions] = useState<Session[] | null>(null);
  const [date, setDate] = useState("");
  const [venue, setVenue] = useState("");
  const [courtCount, setCourtCount] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .today()
      .then(({ sessions: today, defaults }) => {
        setSessions(today);
        // The server's idea of today, in the club's timezone — not the
        // phone's, which is a different date for anyone travelling.
        setDate(defaults.date);
        setVenue(defaults.venue ?? "");
        setCourtCount(defaults.courtCount);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Could not load."));
  }, []);

  return (
    <main className="centre">
      <div className="card stack">
        <h1>Core Baddy</h1>
        <p className="muted">Signed in as {me.displayName}</p>

        {sessions && sessions.length > 0 ? (
          <>
            <h2>Today</h2>
            {sessions.map((session) => (
              <button key={session.id} className="primary" onClick={() => onOpen(session.id)}>
                {formatSessionDate(session.date)} · {session.venue ?? "Session"} ·{" "}
                {session.courtCount} {session.courtCount === 1 ? "court" : "courts"}
              </button>
            ))}
            <p className="muted small">or start another</p>
          </>
        ) : null}

        {/*
          A date, defaulted to today.

          Without one a session could only ever be created for the day it was
          created on, so a Saturday morning that nobody got round to logging
          was unrecoverable, and a session could not be set up the night
          before. `type="date"` gives the platform's own picker, which is the
          one thing on this screen worth not building by hand.
        */}
        <label>
          Date
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>

        <label>
          Venue
          <input value={venue} onChange={(e) => setVenue(e.target.value)} placeholder="SBH Expo" />
        </label>

        <label>
          Courts
          <div className="row">
            {[1, 2, 3].map((n) => (
              <button
                key={n}
                type="button"
                className={n === courtCount ? "chip chip-on" : "chip"}
                onClick={() => setCourtCount(n)}
              >
                {n}
              </button>
            ))}
          </div>
        </label>

        {error ? <p className="error">{error}</p> : null}

        <button
          className="primary"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            setError(null);
            api
              .createSession({
                date: date || undefined,
                venue: venue.trim() || null,
                courtCount,
              })
              .then((session) => onOpen(session.id))
              .catch((e: unknown) =>
                setError(e instanceof Error ? e.message : "Could not start the session."),
              )
              .finally(() => setBusy(false));
          }}
        >
          Start a session
        </button>

        <BackupLink />

        <button
          className="link"
          onClick={() => {
            void api.logout().finally(onSignedOut);
          }}
        >
          Sign out
        </button>
      </div>
    </main>
  );
}

/**
 * The date as the club says it — "Sat 1 Aug", not 2026-08-01.
 *
 * Parsed with an explicit midnight so it is read in the browser's own zone. A
 * bare `new Date("2026-08-01")` is parsed as UTC and renders as 31 July for
 * everybody east of Greenwich, which is the same defect that was shifting
 * session dates on the server.
 */
export function formatSessionDate(date: string): string {
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}
